// components/RemixGalleryLayout.tsx
"use client";
import { useState, useEffect, ChangeEvent } from "react";
import {
  useDisclosure,
  Modal,
  ModalBody,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  Flex,
  Text,
  Image,
  Button,
  CloseButton,
  Icon,
  IconButton,
  Box,
  Input,
  useToast,
  useMediaQuery,
} from "@chakra-ui/react";
// @ts-ignore
import Masonry from "react-responsive-masonry";
import { GrUpload } from "react-icons/gr";
import { FaTrash } from "react-icons/fa";
import { useDropzone } from "react-dropzone";
import { LuImagePlus } from "react-icons/lu";
import { DeleteIcon } from "@chakra-ui/icons";
import heic2any from "heic2any";
import { useQueryClient } from "@tanstack/react-query";
import { useInView } from "react-intersection-observer";
import { useGetRemixGallery, useAdminDeleteRemixGalleryImage } from "queries";
import { RemixGallery, RemixGalleryImage } from "types";
import { fetch as customFetch } from "libs";
import { POST_ADD_REMIX_GALLERY_IMAGE } from "api";

type FileWithPreview = {
  file: File;
  preview: string;
};

type DropzoneProps = {
  files: FileWithPreview[];
  setFiles: React.Dispatch<React.SetStateAction<FileWithPreview[]>>;
};

const Dropzone: React.FC<DropzoneProps> = ({ files, setFiles }) => {
  const onDrop = async (acceptedFiles: File[]) => {
    const newFilesWithPreview = await Promise.all(
      acceptedFiles.map(async (file: File): Promise<FileWithPreview> => {
        let objectURL = URL.createObjectURL(file);

        if (file.type === "" || file.name.toLowerCase().includes(".heic")) {
          await fetch(objectURL)
            .then((res) => res.blob())
            .then((blob) =>
              heic2any({
                blob,
              })
            )
            .then((conversionResult: Blob | Blob[]) => {
              if (conversionResult instanceof Blob) {
                URL.revokeObjectURL(objectURL);
                objectURL = URL.createObjectURL(conversionResult);
              } else {
                if (conversionResult.length > 0) {
                  URL.revokeObjectURL(objectURL);
                  objectURL = URL.createObjectURL(conversionResult[0]);
                }
              }
            })
            .catch((e) => {
              console.log(e);
            });
        }

        return {
          file: file,
          preview: objectURL,
        };
      })
    );

    setFiles((prevFiles) => [...prevFiles, ...newFilesWithPreview]);
  };

  const removeFile = (index: number) => {
    // Revoke the URL of the removed file to avoid memory leaks
    URL.revokeObjectURL(files[index].preview);
    setFiles((prevFiles: FileWithPreview[]) => prevFiles.filter((_s, i) => i !== index));
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "image/png": [],
      "image/jpeg": [],
      "image/webp": [],
      "image/heic": [],
    },
  });

  const thumbs = (files: FileWithPreview[]) =>
    files.map((file, index) => (
      <Box key={index} position="relative">
        <Image src={file.preview} objectFit="contain" />
        <IconButton
          aria-label="Delete image"
          icon={<DeleteIcon />}
          size="xs"
          colorScheme="red"
          position="absolute"
          right="2"
          bottom="2"
          onClick={(e) => {
            e.stopPropagation();
            removeFile(index);
          }}
        />
      </Box>
    ));

  return (
    <div
      {...getRootProps()}
      style={{ marginTop: "20px", border: "2px dashed gray", padding: "20px", textAlign: "center" }}
    >
      <input {...getInputProps()} />
      <Flex minH="200px" direction="column" w="100%" align="center" justify="center">
        {isDragActive && !files.length ? (
          <Text> Drop your files here!</Text>
        ) : (
          <>
            <LuImagePlus size={35} />
            <Text mt="3px" color="#1D71F9" fontWeight={700}>
              Upload files or drag & drop
            </Text>
            <Text mt="3px" mb="3px">
              PNG, JPG, WEBP, and HEIC up to 15MB
            </Text>
            <Masonry columnsCount={3} gutter="2px">
              {thumbs(files)}
            </Masonry>
            <Text mt="3px" fontWeight={700} color="#E20FCE">
              {files.length ? `${files.length} images uploaded` : "Upload images here"}
            </Text>
          </>
        )}
      </Flex>
    </div>
  );
};

interface RemixGalleryImageUrl {
  url: string;
  isValid: boolean;
}

const RemixGalleryLayout = ({
  isAdmin,
  isOpen,
  onClose,
  selectedImage,
  setSelectedImage,
}: {
  isAdmin: boolean;
  isOpen: boolean;
  onClose: () => void;
  selectedImage: RemixGalleryImage | null;
  setSelectedImage: (image: RemixGalleryImage | null) => void;
}) => {
  const { ref, inView } = useInView();
  const { data, hasNextPage, fetchNextPage } = useGetRemixGallery(20);
  const [isTabletOrMobile] = useMediaQuery("(max-width: 992px)");
  const [isXL] = useMediaQuery("(min-width: 1280px)");
  const [isSM] = useMediaQuery("(min-width: 480px)");

  const [remixGalleryColumn, setRemixGalleryColumn] = useState(
    isTabletOrMobile ? (isSM ? 3 : 2) : isXL ? 5 : 4
  );

  useEffect(() => {
    setRemixGalleryColumn(isTabletOrMobile ? (isSM ? 3 : 2) : isXL ? 5 : 4);
  }, [isTabletOrMobile, isXL, isSM]);

  useEffect(() => {
    if (inView && hasNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, fetchNextPage]);

  const [remixGalleryImages, setRemixGalleryImages] = useState<RemixGalleryImage[]>([]);

  useEffect(() => {
    const handleData = async () => {
      if (data) {
        const images = (
          await Promise.all(
            data.pages.map(
              async (page: RemixGallery): Promise<RemixGalleryImage[]> =>
                await Promise.all(
                  page.images.map(async (image: RemixGalleryImage): Promise<RemixGalleryImage> => {
                    if (
                      image.file_name.toLowerCase().includes(".heif") &&
                      !image.sas_url.startsWith("blob:")
                    ) {
                      await fetch(image.sas_url)
                        .then((res) => res.blob())
                        .then((blob) =>
                          heic2any({
                            blob,
                          })
                        )
                        .then((conversionResult: Blob | Blob[]) => {
                          if (conversionResult instanceof Blob) {
                            image.sas_url = URL.createObjectURL(conversionResult);
                          } else {
                            if (conversionResult.length > 0) {
                              image.sas_url = URL.createObjectURL(conversionResult[0]);
                            }
                          }
                        })
                        .catch((e) => {
                          console.log(e);
                        });
                    }

                    return image;
                  })
                )
            )
          )
        ).flat();

        setRemixGalleryImages(images);
      } else {
        setRemixGalleryImages([]);
      }
    };

    handleData();
  }, [data]);

  const {
    isOpen: isAdminUploadOpen,
    onOpen: onAdminUploadOpen,
    onClose: onAdminUploadClose,
  } = useDisclosure();

  const {
    isOpen: isAdminDeleteOpen,
    onOpen: onAdminDeleteOpen,
    onClose: onAdminDeleteClose,
  } = useDisclosure();

  const [remixGalleryImageFiles, setRemixGalleryImageFiles] = useState<FileWithPreview[]>([]);

  const [remixGalleryImageUrls, setRemixGalleryImageUrls] = useState<RemixGalleryImageUrl[]>([
    { url: "", isValid: true },
  ]);

  const adminDeleteRemixGalleryImage = useAdminDeleteRemixGalleryImage();

  const addRemixGalleryImageUrl = () => {
    if (
      remixGalleryImageUrls.length === 0 ||
      remixGalleryImageUrls[remixGalleryImageUrls.length - 1].url !== ""
    ) {
      setRemixGalleryImageUrls([...remixGalleryImageUrls, { url: "", isValid: true }]);
    }
  };

  const updateRemixGalleryImageUrl = (index: number) => (event: ChangeEvent<HTMLInputElement>) => {
    const newArr = [...remixGalleryImageUrls];
    newArr[index] = { url: event.target.value, isValid: true };
    setRemixGalleryImageUrls(newArr);
  };

  const removeRemixGalleryImageUrl = (index: number) => {
    if (remixGalleryImageUrls.length === 1 && index === 0) {
      setRemixGalleryImageUrls([{ url: "", isValid: true }]);
    }

    if (remixGalleryImageUrls.length > 1) {
      setRemixGalleryImageUrls((prevRemixGalleryImageUrls: RemixGalleryImageUrl[]) =>
        prevRemixGalleryImageUrls.filter((_s, i) => i !== index)
      );
    }
  };

  const toast = useToast();
  const queryClient = useQueryClient();
  const [isUploading, setIsUploading] = useState<boolean>(false);

  const handleUpload = async () => {
    try {
      setIsUploading(true);

      const nonEmptyRemixGalleryImageUrls = remixGalleryImageUrls.filter(
        (remixGalleryImageUrl: RemixGalleryImageUrl) => remixGalleryImageUrl.url !== ""
      );

      if (remixGalleryImageFiles.length === 0 && nonEmptyRemixGalleryImageUrls.length === 0) {
        return;
      }

      const remixGalleryImageUrlBlobs = await remixGalleryImageUrls.reduce<Promise<Blob[]>>(
        async (
          accumulatorPromise: Promise<Blob[]>,
          remixGalleryImageUrl: RemixGalleryImageUrl,
          index: number
        ): Promise<Blob[]> => {
          const accumulator = await accumulatorPromise;

          if (remixGalleryImageUrl.url !== "") {
            await fetch(remixGalleryImageUrl.url)
              .then((res) => res.blob())
              .then(async (blob): Promise<Blob | Blob[]> => {
                if (["image/png", "image/jpeg", "image/webp"].includes(blob.type)) {
                  return blob;
                }

                return await heic2any({
                  blob,
                });
              })
              .then((conversionResult: Blob | Blob[]) => {
                if (conversionResult instanceof Blob) {
                  accumulator.push(conversionResult);
                } else {
                  if (conversionResult.length > 0) {
                    accumulator.push(conversionResult[0]);
                  }
                }
              })
              .catch((e) => {
                console.log(e);

                setRemixGalleryImageUrls((prevRemixGalleryImageUrls: RemixGalleryImageUrl[]) =>
                  prevRemixGalleryImageUrls.map((o, i) =>
                    i === index ? { ...o, isValid: false } : o
                  )
                );
              });
          }

          return accumulator;
        },
        Promise.resolve([])
      );

      if (nonEmptyRemixGalleryImageUrls.length !== remixGalleryImageUrlBlobs.length) {
        const hasMultipleErrors =
          nonEmptyRemixGalleryImageUrls.length - remixGalleryImageUrlBlobs.length > 1;

        toast({
          title: "Error",
          description: `Invalid Image URL${
            hasMultipleErrors ? "s" : ""
          } detected, please check your input${hasMultipleErrors ? "s" : ""}.`,
          status: "error",
          duration: 5000,
          isClosable: true,
        });

        return;
      }

      const formData = new FormData();

      remixGalleryImageFiles.forEach((remixGalleryImageFile: FileWithPreview) =>
        formData.append("images", remixGalleryImageFile.file)
      );

      remixGalleryImageUrlBlobs.forEach((remixGalleryImageUrlBlob: Blob) =>
        formData.append("images", remixGalleryImageUrlBlob)
      );

      const response = await customFetch(POST_ADD_REMIX_GALLERY_IMAGE, {
        method: "POST",
        body: formData,
      });

      if (Object.keys(response).length === 0) {
        throw new Error(`Network response was not ok`);
      }

      queryClient.invalidateQueries({ queryKey: ["remixGallery"] });
      console.log("Remix gallery images uploaded successfully", response);
      setRemixGalleryImageFiles([]);
      setRemixGalleryImageUrls([{ url: "", isValid: true }]);
      onAdminUploadClose();

      toast({
        title: "Success",
        description: (response as any).message,
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Error uploading images:", error);

      toast({
        title: "Error",
        description: String(
          (error as any).detail
            ? typeof (error as any).detail === "object"
              ? JSON.stringify((error as any).detail)
              : (error as any).detail
            : error
        ),
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleDelete = () => {
    if (!selectedImage) return;

    adminDeleteRemixGalleryImage.mutate(
      { id: selectedImage.id },
      {
        onSuccess: (response: any) => {
          onAdminDeleteClose();

          toast({
            title: "Success",
            description: (response as any).message,
            status: "success",
            duration: 5000,
            isClosable: true,
          });
        },
        onError: (error: any) => {
          console.error("Error deleting image:", error);

          toast({
            title: "Error",
            description: String(
              (error as any).detail
                ? typeof (error as any).detail === "object"
                  ? JSON.stringify((error as any).detail)
                  : (error as any).detail
                : error
            ),
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        },
      }
    );
  };

  return (
    <Flex>
      <Modal isOpen={isOpen} onClose={onClose} size="full" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalBody bg="#0A0E1D">
            <Flex w="100%" direction="column" alignItems="center">
              <Flex
                w="100%"
                direction="row-reverse"
                position="sticky"
                top="10px"
                alignItems="center"
              >
                <CloseButton onClick={onClose} size="lg" />
                {isAdmin && (
                  <IconButton
                    aria-label="Open admin upload remix gallery image modal"
                    icon={<Icon as={GrUpload} />}
                    bg="transparent"
                    color="white"
                    size="lg"
                    _hover={{}}
                    _active={{}}
                    onClick={onAdminUploadOpen}
                  />
                )}
              </Flex>
              <Flex
                w={{ base: "100%", lg: "90%", xl: "75%" }}
                direction="column"
                alignItems="center"
                position="relative"
              >
                <Text
                  bg="linear-gradient(to right,  #FFF0E1 34%, #A4DBEE 100%)"
                  bgClip="text"
                  color="transparent"
                  fontSize={{ base: "54px", lg: "72px" }}
                  fontWeight="600"
                  mb={{ base: "10px", lg: "20px" }}
                >
                  Gallery
                </Text>
                {remixGalleryImages.length > 0 ? (
                  <>
                    {!isAdmin && (
                      <Text
                        color="white"
                        mb={{ base: "20px", lg: "40px" }}
                        fontSize={{ base: "14px", lg: "16px" }}
                      >
                        Choose the image you want to Remix
                      </Text>
                    )}
                    <Masonry gutter="2px" columnsCount={remixGalleryColumn}>
                      {remixGalleryImages.map((image: RemixGalleryImage, index: number) => (
                        <Flex position="relative" key={index}>
                          <Image
                            src={image.sas_url}
                            objectFit="contain"
                            cursor="pointer"
                            onClick={() => {
                              setSelectedImage(image);

                              if (!isAdmin) {
                                onClose();
                              }
                            }}
                          />
                          {isAdmin && (
                            <IconButton
                              aria-label="Open admin delete remix gallery image modal"
                              icon={<Icon as={FaTrash} />}
                              colorScheme="red"
                              size="xs"
                              _hover={{}}
                              _active={{}}
                              position="absolute"
                              bottom={{ base: "2", md: "5" }}
                              right={{ base: "2", md: "5" }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedImage(image);
                                onAdminDeleteOpen();
                              }}
                            />
                          )}
                        </Flex>
                      ))}
                    </Masonry>
                  </>
                ) : (
                  <Text color="white" fontSize={{ base: "14px", lg: "16px" }}>
                    No image at the moment
                  </Text>
                )}
                <Flex
                  ref={ref}
                  w="100vw"
                  h="1px"
                  bg="transparent"
                  position="absolute"
                  bottom="50vh"
                />
              </Flex>
            </Flex>
            {isAdmin && (
              <>
                <Modal size="xl" isOpen={isAdminUploadOpen} onClose={onAdminUploadClose}>
                  <ModalOverlay />
                  <ModalContent borderRadius={0} bg="#16181F" border="1px solid white">
                    <ModalCloseButton />
                    <Flex
                      justify="center"
                      w="100%"
                      p={10}
                      px={{ base: "30px", md: "80px" }}
                      minH="300px"
                      direction="column"
                    >
                      <Dropzone
                        files={remixGalleryImageFiles}
                        setFiles={setRemixGalleryImageFiles}
                      />
                      {remixGalleryImageUrls.map(
                        (remixGalleryImageUrl: RemixGalleryImageUrl, index: number) => (
                          <Flex key={index} direction="row" alignItems="center" mt="10px">
                            <Input
                              placeholder="Image URL"
                              value={remixGalleryImageUrl.url}
                              onChange={updateRemixGalleryImageUrl(index)}
                              isInvalid={!remixGalleryImageUrl.isValid}
                            />
                            <IconButton
                              aria-label="Delete Image URL"
                              icon={<DeleteIcon />}
                              size="md"
                              colorScheme="red"
                              ml="10px"
                              onClick={(e) => {
                                e.stopPropagation();
                                removeRemixGalleryImageUrl(index);
                              }}
                            />
                          </Flex>
                        )
                      )}
                      <Button
                        mt="10px"
                        bg="white"
                        borderRadius="32px"
                        color="black"
                        onClick={addRemixGalleryImageUrl}
                      >
                        Add Image URL
                      </Button>
                      <Flex>
                        <Button
                          mt="55px"
                          bg="white"
                          borderRadius="32px"
                          color="black"
                          onClick={handleUpload}
                          isDisabled={isUploading}
                          minWidth="150px"
                        >
                          {isUploading ? "Uploading..." : "Upload Image"}
                        </Button>
                      </Flex>
                    </Flex>
                  </ModalContent>
                </Modal>
                <Modal size="xl" isOpen={isAdminDeleteOpen} onClose={onAdminDeleteClose}>
                  <ModalOverlay />
                  <ModalContent borderRadius={0} bg="#16181F" border="1px solid white">
                    <ModalCloseButton />
                    <Flex
                      justify="center"
                      w="100%"
                      p={10}
                      px={{ base: "30px", md: "80px" }}
                      minH="300px"
                      direction="column"
                    >
                      <Text fontSize={{ base: "28px", md: "36px" }} fontWeight={600}>
                        Are you sure you want to delete this image?
                      </Text>

                      <Flex>
                        <Button
                          mt="55px"
                          bg="white"
                          borderRadius="32px"
                          color="black"
                          onClick={handleDelete}
                        >
                          Delete Image
                        </Button>
                      </Flex>
                    </Flex>
                  </ModalContent>
                </Modal>
              </>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Flex>
  );
};

export default RemixGalleryLayout;
